import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { getApiBase } from '@papan/api-client'

/**
 * Post-login continue for the app-mode "Sign in with Papan" round trip (Phase 4b).
 *
 * The broker (on `-api`) sends a logged-out user who clicked "Sign in with Papan" to
 * `…/login?next=/app-signin/<token>`. That `next` is a RELATIVE frontend path so the
 * `safeNext` open-redirect guard accepts it. After they log in, LoginPage lands them
 * HERE, and this route's only job is to hop (client-side) to the `-api` origin's
 * `/share/<token>/authorize-signed-in` — the one place the freshly-set, host-only Better
 * Auth session cookie is visible — which admits them and hands off to the app via
 * `__ppn_ho` (or bounces to the app gate if they turn out not to be a recipient).
 *
 * Why this indirection instead of the broker redirecting straight to `-api`: `safeNext`
 * refuses any cross-origin `next`, so the return must land on a same-origin frontend
 * path first. The destination is built from `getApiBase()` (the SPA's own env-derived
 * API origin) + the path token — never a caller-supplied URL — so there is no
 * open-redirect surface here.
 */
export function AppSignInContinuePage() {
  const { token } = useParams<{ token: string }>()

  useEffect(() => {
    // Token shape guard: share tokens are DNS-label-safe (lowercase alnum + hyphens).
    // Anything else can't be a real app token — send them home rather than build a URL.
    if (!token || !/^[a-z0-9-]{1,120}$/.test(token)) {
      window.location.replace('/')
      return
    }
    window.location.replace(`${getApiBase()}/share/${encodeURIComponent(token)}/authorize-signed-in`)
  }, [token])

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#71717a', fontFamily: 'ui-sans-serif, system-ui, sans-serif', fontSize: 14 }}>
      Signing you in…
    </div>
  )
}
